import { useParams, Link } from "react-router-dom";
import { blogs } from "../data/blogsData";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

export default function BlogSingle() {

  const { slug } = useParams();
  const post = blogs.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!post) {
    return <div className="py-20 text-center">Blog not found</div>;
  }

  const url = `https://softcoders.in/blog/${post.slug}`;

  return (
    <div className="bg-[#f7f8fc] min-h-screen">

      {/* ================= SEO ================= */}
      <Helmet>
        <title>{post.title} | Soft Coders</title>

        <meta name="description" content={post.desc} />
        <link rel="canonical" href={url} />

        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.desc} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.desc} />
        <meta name="twitter:image" content={post.image} />
      </Helmet>

      {/* Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.desc,
          image: post.image,
          author: {
            "@type": "Organization",
            name: "Soft Coders"
          },
          publisher: {
            "@type": "Organization",
            name: "Soft Coders"
          },
          mainEntityOfPage: url
        })}
      </script>

      {/* ===== HERO ===== */}
      <div className="max-w-6xl mx-auto px-4 pt-6 pb-6">

        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
          {post.title}
        </h1>

        <div className="flex items-center gap-3 mt-3 text-sm text-gray-500">
          <span className="text-blue-600 font-medium">{post.category}</span>
          <span>•</span>
          <span>{post.date}</span>
          <span>•</span>
          <span>{post.readTime}</span>
        </div>

      </div>

      {/* ===== MAIN GRID ===== */}
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* ===== CONTENT ===== */}
        <div className="lg:col-span-2 bg-white p-5 sm:p-8 rounded-2xl shadow-sm">

          {/* IMAGE */}
          <img
            src={post.image || "/images/default.jpg"}
            alt={post.title}
            className="w-full h-[220px] sm:h-[320px] object-cover rounded-xl mb-6"
          />

          {/* CONTENT */}
          <div className="space-y-5 text-gray-700 leading-relaxed text-[15px] sm:text-base">

            {post.content.map((block, i) => {
              if (block.type === "heading") {
                return (
                  <h2 key={i} className="text-xl sm:text-2xl font-semibold">
                    {block.text}
                  </h2>
                );
              }

              if (block.type === "paragraph") {
                return <p key={i}>{block.text}</p>;
              }

              if (block.type === "list") {
                return (
                  <ul key={i} className="list-disc pl-6 space-y-2">
                    {block.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                );
              }

              return null;
            })}

          </div>

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-500 text-white px-6 py-10 sm:px-10 sm:py-12 rounded-2xl text-center shadow-lg">

            <h3 className="text-xl sm:text-2xl font-bold mb-3">
              Need a Website or Software?
            </h3>

            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Let Soft Coders build a powerful, scalable solution tailored for your business growth.
            </p>

            <Link
              to="/contact"
              className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-semibold shadow-md hover:scale-105 transition"
            >
              Contact Us
            </Link>

          </div>

        </div>

        {/* ===== SIDEBAR ===== */}
        <div className="space-y-6">

          {/* SEARCH */}
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <input
              type="text"
              placeholder="Search blog..."
              className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* POPULAR */}
          <div className="bg-white p-5 rounded-2xl shadow-sm">
            <h3 className="text-lg font-semibold mb-4">
              Popular Posts
            </h3>

            <div className="space-y-4">
              {blogs.slice(0, 3).map((item) => (
                <Link
                  key={item.id}
                  to={`/blog/${item.slug}`}
                  className="flex gap-3 group"
                >
                  <img
                    src={item.image || "/images/default.jpg"}
                    alt={item.title}
                    className="w-20 h-16 object-cover rounded-lg"
                  />

                  <div>
                    <p className="text-sm font-medium group-hover:text-blue-600">
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.date}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

        </div>

      </div>

      {/* ===== MOBILE CTA ===== */}
      <div className="fixed bottom-0 left-0 w-full bg-white shadow-lg px-4 py-3 flex justify-between items-center lg:hidden z-50 border-t">
        <p className="text-sm font-medium">Need a Website?</p>
        <Link
          to="/contact"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
        >
          Contact
        </Link>
      </div>

    </div>
  );
}